'use client';

import { useEffect, useRef, type ReactNode } from 'react';
import type { CustomerSupportProps, ComposeMessageLabels } from './types';
import { Image, IMAGE_LOADING, CUSTOMER_SUPPORT_SECTION_ID } from 'components';
import { ContactArea, ContactForm, ContactFormConfirmation, ContactHeader } from './partials';
import { analytics } from '@pixum/tracking';
import Text, { TEXT_VARIANT } from '@pixum/text';
import { removeEmptySpaces } from 'utils';
import { useUIStore, FormStatus } from 'stores/ui';
import { CANCELLATION_TOPIC_KEYS } from 'data-layer/constants';
import { CLIENT_AUTH_STATUS, useClientAuthentication } from 'hooks';
import styles from './CustomerSupportClient.module.css';

interface CustomerSupportClientProps extends CustomerSupportProps {
  cancellationSlot: ReactNode;
}

const HEADLINE_ID = `${CUSTOMER_SUPPORT_SECTION_ID}-headline`;

export function CustomerSupportClient({
  constants,
  device,
  thumbnail,
  logo,
  phone,
  whatsappPhone,
  email,
  whatsappAvailability,
  weekdayOpening,
  weekendOpening,
  topics,
  serviceBotRoute,
  locale,
  cancellationSlot,
  autoOpenContactForm,
}: CustomerSupportClientProps) {
  const {
    contactForm,
    setContactFormStatus,
    resetContactForm,
    setContactFormSubjectToCancellation,
    setContactFormFields,
  } = useUIStore(state => state);

  const { status: authStatus, isAuthenticated } = useClientAuthentication();
  const contactFormStatus = contactForm.status;

  const formRef = useRef<HTMLFormElement>(null);
  const triggerFocusRef = useRef<Element | null>(null);
  const emailTriggerWrapperRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!autoOpenContactForm) return;
    setContactFormStatus(FormStatus.visible);
  }, [autoOpenContactForm]);

  useEffect(() => {
    if (!autoOpenContactForm) return;
    if (!contactForm.availableSubjects?.length) return;

    setContactFormSubjectToCancellation();

    const cancellationTopic = topics.find(t => t.internalName === CANCELLATION_TOPIC_KEYS[0]);

    if (cancellationTopic?.prefilledMessage !== undefined) {
      setContactFormFields({ text: cancellationTopic.prefilledMessage });
    }
  }, [autoOpenContactForm, contactForm.availableSubjects]);

  useEffect(() => {
    if (contactFormStatus === FormStatus.visible) {
      formRef.current?.focus({ preventScroll: true });
    }

    if (contactFormStatus === FormStatus.hidden && triggerFocusRef.current instanceof HTMLElement) {
      triggerFocusRef.current = null;
      // Find the first focusable element inside the email trigger wrapper (freshly mounted).
      const emailTrigger = emailTriggerWrapperRef.current?.querySelector<HTMLElement>('[tabindex], button, a[href]')
        ?? emailTriggerWrapperRef.current;
      emailTrigger?.focus();
    }
  }, [contactFormStatus]);

  // Main titles and subtitles
  const title = constants.i18n_customer_service;
  const subtitle = constants.i18n_questions;
  const serviceBotTitle = constants.i18n_chatbot;
  const serviceBotSubtitle = constants.i18n_chat_now;
  const phoneTitle = constants.i18n_by_phone;
  const whatsAppTitle = constants.i18n_whatsapp;
  const emailTitle = constants.i18n_email_address;

  // Contact Form texts
  const formConstants = {
    nameLabel: constants.i18n_your_name,
    emailLabel: constants.i18n_your_email_address,
    subjectLabel: constants.i18n_subject_your_contact,
    textLabel: constants.i18n_your_message_to_us,
    cancelLabel: constants.i18n_my_pixum_cancel,
    sendLabel: constants.i18n_send,
    chooseYourTopic: constants.i18n_choose_your_topic,
    cancelContractLabel: constants.i18n_cancel_contract,
    cancelOrderLoginBannerText: constants.i18n_cancel_order_login_banner_text,
    cancelOrderLoginBannerButton: constants.i18n_cancel_order_login_banner_button,
    emailEmptyErrorLabel: constants.i18n_email_empty,
    emailInvalidErrorLabel: constants.i18n_email_invalid,
    cancelOrderRequiredErrorLabel: constants.i18n_cancel_order_required_error,
    cancelProductsRequiredErrorLabel: constants.i18n_cancel_products_required_error,
    cancelProductsPerOrderRequiredErrorLabel: constants.i18n_cancel_products_per_order_required_error,
  };

  const composeLabels: ComposeMessageLabels = {
    canceledOrdersHeading: constants.i18n_cancel_table_orders_heading,
    orderPrefix: constants.i18n_cancel_order_item_label,
    submittedLabel: constants.i18n_cancel_table_submitted_label,
    positionHeader: constants.i18n_cancel_table_position_header,
    articleNameHeader: constants.i18n_cancel_table_article_name_header,
    productIdHeader: constants.i18n_cancel_table_product_id_header,
    priceHeader: constants.i18n_cancel_table_price_header,
  };

  // Confirmation texts
  const confirmationConstants = {
    successHeadline: constants.i18n_confirmation_thank_you_message,
    successText: constants.i18n_confirmation_acknowledgment_receipt,
    close: constants.i18n_shut_down,
  };

  // Availability texts
  const serviceBotAvailability = [constants.i18n_chat_availabilityi18n_currently_not_available].filter(Boolean);
  const waAvailability = [whatsappAvailability].filter(Boolean);
  const officeAvailability = [weekdayOpening, weekendOpening].filter(Boolean);

  // Determine which contact options to show
  const showServiceBot = !!serviceBotRoute && !!serviceBotTitle;
  const showPhone = !!phone;
  const showWhatsApp = !!whatsappPhone && waAvailability.length > 0;
  const showEmail = !!email;

  const handleTrackingClick = (action: string, label: string) => {
    analytics.trackEvent('service', action, label);
  };

  const handleClickEmailArea = (e: React.MouseEvent<HTMLElement>) => {
    e.preventDefault();
    triggerFocusRef.current = document.activeElement;
    setContactFormStatus(FormStatus.visible);
  };

  const handleCancel = () => {
    setContactFormStatus(FormStatus.hidden);
  };

  const handleSubmitSuccess = () => {
    setContactFormStatus(FormStatus.successful);
    resetContactForm();
  };

  return (
    <section className={`${styles.customerSupport} main-section center--content`} id={CUSTOMER_SUPPORT_SECTION_ID} aria-labelledby={HEADLINE_ID}>
      <Image className={styles.image} device={device} image={thumbnail} loading={IMAGE_LOADING.LAZY} />
      <Text id={HEADLINE_ID} className={styles.headline} variant={TEXT_VARIANT.TITLE_3}>
        {title}
      </Text>
      <div
        className={styles.content}
        aria-live="polite"
        aria-label={constants.i18n_a11y_customer_support_live_region_label}
      >
        <span className="visually-hidden" role="status">
          {contactFormStatus === FormStatus.visible
            ? constants.i18n_a11y_customer_support_form_opened
            : contactFormStatus === FormStatus.successful
            ? constants.i18n_a11y_customer_support_form_confirmed
            : ''}
        </span>
        {contactFormStatus === FormStatus.hidden && (
          <div className={styles.contact}>
            <ContactHeader subtitle={subtitle} logo={logo} device={device} />
            {showServiceBot && (
              <ContactArea
                title={serviceBotTitle}
                subtitle={serviceBotSubtitle}
                icon="chatbot"
                href={serviceBotRoute || ''}
                availability={serviceBotAvailability}
                masked
                onClick={() => handleTrackingClick('service-bot', 'klick')}
              />
            )}
            {showPhone && (
              <ContactArea
                title={phoneTitle}
                subtitle={phone}
                icon="phone"
                href={`tel:${removeEmptySpaces(phone)}`}
                availability={officeAvailability}
                onClick={() => handleTrackingClick('telefon', 'klick')}
                openLinkInNewTab
              />
            )}
            {showWhatsApp && (
              <ContactArea
                title={whatsAppTitle}
                subtitle={whatsappPhone}
                icon="whatsapp"
                href={`https://wa.me/${removeEmptySpaces(whatsappPhone)}`}
                availability={waAvailability}
                onClick={() => handleTrackingClick('whatsapp', 'klick')}
                openLinkInNewTab
              />
            )}
            {showEmail && (
              <ContactArea
                ref={emailTriggerWrapperRef}
                className={styles.contactEmail}
                title={emailTitle}
                subtitle={email}
                icon="email"
                href={`mailto:${email}`}
                availability={showPhone ? [] : officeAvailability}
                onClick={handleClickEmailArea}
              />
            )}
          </div>
        )}
        {contactFormStatus === FormStatus.visible && (
          <div className={styles.form}>
            <ContactForm
              formRef={formRef}
              device={device}
              formConstants={formConstants}
              topics={topics}
              cancellationSlot={cancellationSlot}
              isAuthenticated={isAuthenticated}
              isAuthLoading={authStatus === CLIENT_AUTH_STATUS.LOADING}
              onCancel={handleCancel}
              onSubmitSuccess={handleSubmitSuccess}
              locale={locale}
              composeLabels={composeLabels}
            />
          </div>
        )}

        {contactFormStatus === FormStatus.successful && (
          <ContactFormConfirmation confirmationConstants={confirmationConstants} onCancel={handleCancel} />
        )}
      </div>
    </section>
  );
}
